/**
 * 📦 Vinted-Namen (Sonderfunktion „vintedImport", z. B. Green Goat)
 *
 * Vinted-Go-Labels tragen keinen Empfängernamen — PaketPilot legt sie als „Vinted 300-352"
 * an (letzte 6 Ziffern der Sendungsnummer). Die Vinted-Partner-App zeigt dieselbe Kurznummer
 * MIT Namen. Aus einem Screenshot dieser Liste (ML /vinted-list) kommen Paare
 * {code:'300352', name:'Josje Heezen'}; hier werden sie
 *   1) 24 h gemerkt → neue Vinted-Pakete bekommen beim Erfassen sofort den echten Namen,
 *   2) auf vorhandene Platzhalter-Pakete angewendet.
 * Reine Funktionen (ohne React) → per Node testbar.
 */

const KEY = 'kiosk_vinted_names';
const TTL_MS = 24 * 60 * 60 * 1000;
export const PLATZHALTER_RE = /^Vinted (\d{3})-(\d{3})$/;

/** „300-352", „... 300-352", „VGS0000062 240-305", 1788…300352 → „300352" (letzte 6 Ziffern) */
export function normCode(x) {
  const d = String(x || '').replace(/\D/g, '');
  return d.length >= 6 ? d.slice(-6) : '';
}

export function formatCode(code6) {
  const c = normCode(code6);
  return c ? `${c.slice(0, 3)}-${c.slice(3)}` : '';
}

export function platzhalterName(code6) {
  const f = formatCode(code6);
  return f ? `Vinted ${f}` : '';
}

/** Kurznummer eines Platzhalter-Pakets („Vinted 300-352") oder '' */
export function platzhalterCode(name) {
  const m = String(name || '').trim().match(PLATZHALTER_RE);
  return m ? m[1] + m[2] : '';
}

function load(now = Date.now()) {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '{}') || {};
    const out = {};
    for (const [c, v] of Object.entries(raw)) {
      if (v && v.name && now - (v.at || 0) < TTL_MS) out[c] = v;
    }
    return out;
  } catch { return {}; }
}

/** Paare merken (24 h). Rückgabe: Anzahl gemerkter Einträge insgesamt. */
export function rememberPairs(pairs, now = Date.now()) {
  const map = load(now);
  for (const p of pairs || []) {
    const c = normCode(p && p.code);
    const n = String((p && p.name) || '').trim();
    if (c && n) map[c] = { name: n, at: now };
  }
  try { localStorage.setItem(KEY, JSON.stringify(map)); } catch { /* */ }
  return Object.keys(map).length;
}

/** Gemerkter Name zur Kurznummer oder '' */
export function lookupName(code, now = Date.now()) {
  const c = normCode(code);
  const v = c ? load(now)[c] : null;
  return v ? v.name : '';
}

/** Nur Pakete im aktuellen Bestand, die noch den Platzhalternamen tragen */
/** Kurznummer eines Platzhalter-Pakets: aus dem Namen „Vinted 123-456" oder — bei einem Fehl-Namen wie der
 *  Restzeit „01:30:18" (07.10.2026 aus Vinteds Liste übernommen) — aus der Sendungsnummer. */
export function platzhalterPaketCode(p) {
  if (!p) return '';
  const c = platzhalterCode(p.name);
  if (c) return c;
  // Fehl-Namen aus Vinteds Liste: Restzeit („01:30:18") oder Bedientext („Scan parcels") — 07.10.2026
  return /^(\d{1,2}:\d{2}:\d{2}|Scan parcels?|Prepare for (recipient|driver)|Loading|Parcels)$/.test(String(p.name || '').trim())
    ? (vintedCodeAusTracking(p.trackingNumber) || glsInVintedCode(p.trackingNumber, p.paketdienst)) : '';
}

export function offenePlatzhalter(pakete) {
  return (pakete || []).filter((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt
    && platzhalterPaketCode(p));
}

/**
 * Welche Platzhalter-Pakete bekommen welchen Namen?
 * @returns {Array<{paket, code, name}>}
 */
export function planUpdates(pakete, pairs) {
  const map = new Map();
  for (const p of pairs || []) {
    const c = normCode(p && p.code);
    const n = String((p && p.name) || '').trim();
    if (c && n) map.set(c, n);
  }
  const out = [];
  for (const paket of offenePlatzhalter(pakete)) {
    const c = platzhalterPaketCode(paket);
    if (map.has(c)) out.push({ paket, code: c, name: map.get(c) });
  }
  return out;
}

// ── Foto vom BILDSCHIRM der Vinted-App statt vom Etikett ────────────────────
// Nach dem Scan zeigt die Vinted-Go-App „Details pakket": ID, Empfänger, Status. Wird dieser
// Bildschirm als Paket-Foto aufgenommen, liest die normale Etikett-Erkennung Unsinn
// („Waiting forrecipient /inted Go Babet de Graaf" als Name, Green Goat 01.10.2026) oder gar nichts.
// Der Listen-Leser (/vinted-list) liest denselben Bildschirm zuverlässig.

const STATUS_RE = /waiting\s*for\s*recipient|wachten\s*op\s*ontvanger|wartet\s*auf\s*empf|en\s*attente\s*d[ue]|inted\s*go\b|details?\s*pakket|parcel\s*details/i;

/** Sendungsnummer eines Vinted-Pakets? (16 Ziffern mit 17… oder „VGS…") → Kurznummer „789160", sonst '' */
export function vintedCodeAusTracking(tracking) {
  const t = String(tracking || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return (/^17\d{14}$/.test(t) || /^VGS\d{6,}$/.test(t)) ? t.slice(-6) : '';
}

/**
 * GLS-Partnerpaket innerhalb von Vinted („GLS Parcels to People", Strichcode mit 11–12 Ziffern, seit 09.10.2026 über
 * „Vinted erfassen" angenommen): die Vinted-App führt es wie jedes andere Paket unter den letzten 6 Ziffern.
 * Nur mit Paketdienst Vinted — ein normales GLS-Paket hat keine Vinted-Kurznummer. → 6 Ziffern oder ''
 */
export function glsInVintedCode(tracking, paketdienst) {
  const t = String(tracking || '').replace(/[^A-Za-z0-9]/g, '');
  return /vinted/i.test(String(paketdienst || '')) && /^\d{11,12}$/.test(t) ? t.slice(-6) : '';
}

/** Vinted-Kurznummer eines Pakets: aus der Sendungsnummer (17…, VGS…, GLS in Vinted) oder dem Platzhalternamen, sonst '' */
export function paketKurzCode(p) {
  if (!p) return '';
  return vintedCodeAusTracking(p.trackingNumber) || platzhalterCode(p.name) || glsInVintedCode(p.trackingNumber, p.paketdienst);
}

/**
 * Sieht das Erkennungs-Ergebnis nach einem Vinted-App-Bildschirm aus?
 *  - der „Name" enthält Statuswörter der App, oder
 *  - es wurde gar nichts erkannt (kein Name, keine Straße, keine Sendungsnummer).
 * Ein echtes Vinted-ETIKETT (kein Name, aber Sendungsnummer) zählt NICHT — das läuft wie bisher.
 */
export function istVintedBildschirm({ name, street, tracking } = {}) {
  const n = String(name || '').trim();
  if (n && STATUS_RE.test(n)) return true;
  return !n && !String(street || '').trim() && !String(tracking || '').trim();
}

/**
 * Soll der Listen-Leser (/vinted-list) zusätzlich gefragt werden?
 *  - das Foto sieht nach dem Bildschirm der Vinted-App aus (siehe oben), oder
 *  - als Paketdienst wurde Vinted erkannt, aber keine Vinted-Nummer: Die Etikett-Erkennung liest vom
 *    Bildschirm manchmal nur den Namen (Green Goat 02.10.2026: erstes Etikett ohne „309-301") —
 *    ohne Nummer fehlt sie auf dem Etikett und das Paket ist bei der Abholung schwer zu finden.
 * Ein echtes Vinted-Etikett hat immer eine Sendungsnummer → keine zweite Anfrage.
 */
export function brauchtVintedNachfrage({ name, street, tracking, paketdienst } = {}) {
  if (istVintedBildschirm({ name, street, tracking })) return true;
  if (!/vinted/i.test(String(paketdienst || '')) || vintedCodeAusTracking(tracking)) return false;
  const t = String(tracking || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (t.length >= 10 && !/^(17|VGS)/.test(t)) return false;
  if (String(name || '').trim() && String(street || '').trim()) return false;
  return true;
}

/**
 * Ergebnis des Listen-Lesers für EIN fotografiertes Paket verwerten.
 * Nur wenn genau ein vollständiges Paar (Kurznummer + Name) gelesen wurde — sonst null (nie raten).
 * tracking: die von der Etikett-Erkennung gelesene Nummer; passt sie zur Kurznummer, bleibt sie,
 * sonst „VGS" + Kurznummer (die volle ID ist dann nicht bekannt, die Kurznummer genügt zum Finden).
 */
export function ausVintedBildschirm(eintraege, tracking) {
  const gut = (eintraege || []).filter((e) => e && normCode(e.code) && String(e.name || '').trim());
  if (gut.length !== 1) return null;
  const code = normCode(gut[0].code);
  const t = String(tracking || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return { name: String(gut[0].name).trim(), code, tracking: vintedCodeAusTracking(t) === code ? t : `VGS${code}` };
}

// ── Vinted nach Nummer einsortieren (Option im Setup, Standard: aus) ─────────
// Vinted-Etiketten tragen keinen Namen. Statt ihn nachzutragen, kann ein Shop Vinted-Pakete nach
// der Kurznummer ablegen: Fach = „V" + letzte Ziffer (V0–V9), die Nummer steht groß auf dem Etikett.
// Bei der Abholung zeigt die Vinted-App dieselbe Nummer → Fach und Paket sind sofort gefunden.
const SORT_KEY = 'kiosk_vinted_sort';

export function isVintedSortOn() {
  try {
    const cs = String(localStorage.getItem('kiosk_carrier_sort') || '');
    const m = cs.match(/(?:^|;)vinted:(hausnummer|nachname|kurznummer)(?:;|$)/);
    if (m) return m[1] === 'kurznummer';
    return localStorage.getItem(SORT_KEY) === '1';
  } catch { return false; }
}
export function setVintedSortOn(on) {
  try { localStorage.setItem(SORT_KEY, on ? '1' : '0'); } catch { /* */ }
}

/** Fach für ein Vinted-Paket nach Kurznummer: „789160" → „V0"; ohne gültige Nummer '' */
export function vintedFach(code6) {
  const c = normCode(code6);
  return c ? `V${c.slice(-1)}` : '';
}
